import { CheckIcon, PlusIcon } from "@heroicons/react/24/outline"
import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  SUGGESTED_TAG_SOURCE_LABELS,
  type SuggestedTag,
  type SuggestedTagSource,
} from "~/ui-rtk/api/paidMediaApi"
import {
  useAssignTagMutation,
  useBulkGetTagAssignmentsQuery,
} from "~/ui-rtk/api/tagAssignmentsApi"
import { MarkdownContent } from "~/ui-rtk/components/chatbot/MarkdownContent"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import { H5, H6 } from "~/ui-rtk/components/ui/typography"
import useMediaTagAccess from "~/ui-rtk/hooks/media-tag-access"
import { useTagCategories } from "~/ui-rtk/hooks/use-structured-tags"
import type { TagCategory, TagOption } from "~/ui-rtk/types/structured-tags"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

/** The analysis a details drawer shows: a fresh job row or the saved copy. */
export interface DisplayedCreativeAnalysis {
  mediaType: "video" | "image" | "text"
  analysis: string
  suggestedTags?: SuggestedTag[]
  suggestedMediaCategory?: string
  analyzedAt?: string | null
}

interface CreativeAnalysisPanelProps {
  itemType: "ad" | "post"
  itemId: string
  analysis: DisplayedCreativeAnalysis | null
  isAnalyzing: boolean
  /** The report was saved by the run that just finished. */
  isSaved: boolean
  onAnalyze: () => void
  /** Header for a `text` (metadata / caption only) analysis. */
  textAnalysisTitle: string
  /**
   * The ad media-category suggestion (ads only): the suggested value, the
   * item's current one, and how to apply it. Omit to hide the row.
   */
  mediaCategory?: {
    suggested?: string
    current: string | null | undefined
    apply: () => Promise<void>
  }
}

/**
 * "Analyze with AI" for a details drawer: the analyze button, the Gemini
 * report, and its taxonomy tag suggestions with one-click apply (per option
 * and "Apply all"). Shared by the ad and organic-post drawers so both read
 * and tag the same way; the caller owns running the analysis job.
 */
export default function CreativeAnalysisPanel({
  itemType,
  itemId,
  analysis,
  isAnalyzing,
  isSaved,
  onAnalyze,
  textAnalysisTitle,
  mediaCategory,
}: CreativeAnalysisPanelProps) {
  const isMediaTagsEnabled = useMediaTagAccess()
  const { categories: tagCategories } = useTagCategories()
  const { data: tagAssignments } = useBulkGetTagAssignmentsQuery(
    { itemType, itemIds: [itemId] },
    { skip: !isMediaTagsEnabled || !itemId },
  )

  const [assignTag] = useAssignTagMutation()
  const [applyingOptionIds, setApplyingOptionIds] = useState<string[]>([])
  // Options applied from this analysis. The assignTag invalidation refetches
  // the bulk-get that feeds `tagAssignments`, but that round-trip lags the
  // mutation — without this the chip would flick back to "apply" in between.
  const [appliedOptionIds, setAppliedOptionIds] = useState<string[]>([])
  const [isApplyingMediaCategory, setIsApplyingMediaCategory] = useState(false)
  const [isApplyingAllSuggestions, setIsApplyingAllSuggestions] =
    useState(false)

  // The drawer is a persistent instance — browsing to another item changes
  // the id without remounting; forget the previous item's applied chips.
  useEffect(() => {
    setApplyingOptionIds([])
    setAppliedOptionIds([])
  }, [itemId])

  // Resolve the analysis' label-based tag suggestions against the live
  // taxonomy. Both sides come from the same tag-categories source and the
  // server already canonicalized the labels, so a case-insensitive label
  // match recovers the option ids needed to actually assign them.
  const suggestedTagRows = useMemo(() => {
    if (!analysis?.suggestedTags?.length) {
      return []
    }
    const assignedOptionIds = new Set([
      ...Object.values(tagAssignments?.[itemId] ?? {})
        .flat()
        .map(assignment => assignment.optionId),
      ...appliedOptionIds,
    ])
    const rows: Array<{
      category: TagCategory
      reason?: string
      source?: SuggestedTagSource
      options: Array<TagOption & { isApplied: boolean }>
    }> = []
    for (const suggestion of analysis.suggestedTags) {
      const category = tagCategories.find(
        c => c.label.toLowerCase() === suggestion.category.toLowerCase(),
      )
      if (!category) {
        continue
      }
      const options = suggestion.options
        .map(label =>
          category.options.find(
            o => o.label.toLowerCase() === label.toLowerCase(),
          ),
        )
        .filter((o): o is TagOption => !!o)
        .map(o => ({ ...o, isApplied: assignedOptionIds.has(o.id) }))
      if (options.length > 0) {
        rows.push({
          category,
          reason: suggestion.reason,
          source: suggestion.source,
          options,
        })
      }
    }
    return rows
  }, [analysis, tagCategories, tagAssignments, itemId, appliedOptionIds])

  const suggestedMediaCategory = mediaCategory?.suggested
  const isMediaCategoryApplied =
    !!suggestedMediaCategory &&
    mediaCategory?.current === suggestedMediaCategory

  const showTagSuggestions =
    isMediaTagsEnabled &&
    (suggestedTagRows.length > 0 || !!suggestedMediaCategory)

  // When the structured section below renders the suggestions with apply
  // buttons, drop the server-appended "Suggested Tags" markdown block so the
  // same list isn't shown twice.
  const analysisMarkdown = useMemo(() => {
    if (!analysis) {
      return ""
    }
    if (!showTagSuggestions) {
      return analysis.analysis
    }
    const idx = analysis.analysis.indexOf("**Suggested Tags**")
    return idx === -1
      ? analysis.analysis
      : analysis.analysis.slice(0, idx).trimEnd()
  }, [analysis, showTagSuggestions])

  const handleApplySuggestedTag = async (
    categorySlug: string,
    optionId: string,
  ) => {
    setApplyingOptionIds(prev => [...prev, optionId])
    try {
      await assignTag({ categorySlug, optionId, itemType, itemId }).unwrap()
      setAppliedOptionIds(prev => [...prev, optionId])
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message
      toast.error(message ?? "Failed to apply the tag")
    } finally {
      setApplyingOptionIds(prev => prev.filter(id => id !== optionId))
    }
  }

  const handleApplySuggestedMediaCategory = async () => {
    if (!mediaCategory || !suggestedMediaCategory) {
      return
    }
    setIsApplyingMediaCategory(true)
    try {
      await mediaCategory.apply()
    } catch {
      toast.error("Failed to apply the media category")
    } finally {
      setIsApplyingMediaCategory(false)
    }
  }

  const hasUnappliedSuggestions =
    suggestedTagRows.some(row => row.options.some(o => !o.isApplied)) ||
    (!!suggestedMediaCategory && !isMediaCategoryApplied)

  // Sequential on purpose: each per-item handler carries its own spinner and
  // error toast, and one failure must not stop the rest (they never throw).
  const handleApplyAllSuggestions = async () => {
    setIsApplyingAllSuggestions(true)
    try {
      if (suggestedMediaCategory && !isMediaCategoryApplied) {
        await handleApplySuggestedMediaCategory()
      }
      for (const row of suggestedTagRows) {
        for (const option of row.options) {
          if (option.isApplied) {
            continue
          }
          await handleApplySuggestedTag(row.category.slug, option.id)
        }
      }
    } finally {
      setIsApplyingAllSuggestions(false)
    }
  }

  const sourceHint =
    itemType === "post"
      ? "This tag came from the post's caption, not from the media itself"
      : "This tag came from the ad's metadata, not from the creative itself"

  return (
    <>
      <button
        onClick={onAnalyze}
        disabled={isAnalyzing}
        className={cn(
          "mt-3 gap-2 w-full justify-center items-center p-4 bg-basic-dark-blue border border-basic-blue flex rounded-lg text-4 line-height-4 font-semibold leading-5",
          isAnalyzing
            ? "opacity-70 cursor-default"
            : "cursor-pointer transition-all hover:bg-basic-blue active:scale-95",
        )}
      >
        {isAnalyzing ? (
          <>
            <Loader size={20} />
            Analyzing creative… this can take a minute
          </>
        ) : analysis ? (
          "Re-analyze Creative with AI"
        ) : (
          "Analyze Creative with AI"
        )}
      </button>
      {analysis && (
        <Card
          rounded
          className="mt-3 bg-basic-dark-blue border border-basic-blue"
        >
          <div className="flex justify-between items-center mb-2">
            <H5 className="font-bold">
              {analysis.mediaType === "video"
                ? "Video Analysis"
                : analysis.mediaType === "text"
                  ? textAnalysisTitle
                  : "Image Analysis"}
            </H5>
            <span className="text-xs text-basic-grey-inactive">
              {isSaved
                ? "Saved"
                : analysis.analyzedAt
                  ? `Analyzed ${new Date(analysis.analyzedAt).toLocaleDateString()}`
                  : null}
            </span>
          </div>
          <div className="text-sm">
            <MarkdownContent>{analysisMarkdown}</MarkdownContent>
          </div>
          {showTagSuggestions && (
            <div className="mt-3 pt-3 border-t border-basic-blue">
              <div className="flex justify-between items-center mb-2">
                <H6 className="font-semibold">Suggested Tags</H6>
                {hasUnappliedSuggestions && (
                  <button
                    onClick={() => void handleApplyAllSuggestions()}
                    disabled={isApplyingAllSuggestions}
                    className={cn(
                      "inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg border border-basic-blue transition-all",
                      isApplyingAllSuggestions
                        ? "opacity-70 cursor-default"
                        : "cursor-pointer hover:bg-basic-blue active:scale-95",
                    )}
                  >
                    {isApplyingAllSuggestions ? (
                      <Loader size={12} />
                    ) : (
                      <CheckIcon className="w-3 h-3" />
                    )}
                    Apply all
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {suggestedMediaCategory && (
                  <div className="text-sm">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-basic-grey-inactive">
                        Media Category:
                      </span>
                      <button
                        onClick={() => void handleApplySuggestedMediaCategory()}
                        disabled={
                          isApplyingMediaCategory || isMediaCategoryApplied
                        }
                        className={cn(
                          "inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg border transition-all",
                          isMediaCategoryApplied
                            ? "border-basic-grey-inactive text-basic-grey-inactive cursor-default"
                            : "border-basic-blue cursor-pointer hover:bg-basic-blue active:scale-95",
                        )}
                      >
                        {isApplyingMediaCategory ? (
                          <Loader size={12} />
                        ) : isMediaCategoryApplied ? (
                          <CheckIcon className="w-3 h-3" />
                        ) : (
                          <PlusIcon className="w-3 h-3" />
                        )}
                        {suggestedMediaCategory}
                      </button>
                    </div>
                  </div>
                )}
                {suggestedTagRows.map(row => (
                  <div key={row.category.id} className="text-sm">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-basic-grey-inactive">
                        {row.category.label}:
                      </span>
                      {row.options.map(option => {
                        const isApplying = applyingOptionIds.includes(option.id)
                        return (
                          <button
                            key={option.id}
                            onClick={() =>
                              void handleApplySuggestedTag(
                                row.category.slug,
                                option.id,
                              )
                            }
                            disabled={option.isApplied || isApplying}
                            className={cn(
                              "inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg border transition-all",
                              option.isApplied
                                ? "border-basic-grey-inactive text-basic-grey-inactive cursor-default"
                                : "border-basic-blue cursor-pointer hover:bg-basic-blue active:scale-95",
                            )}
                          >
                            {isApplying ? (
                              <Loader size={12} />
                            ) : option.isApplied ? (
                              <CheckIcon className="w-3 h-3" />
                            ) : (
                              <PlusIcon className="w-3 h-3" />
                            )}
                            {option.label}
                          </button>
                        )
                      })}
                    </div>
                    {(row.reason ||
                      (row.source && row.source !== "creative")) && (
                      <div className="text-xs text-basic-grey-inactive mt-0.5">
                        {row.source && row.source !== "creative" && (
                          <span className="italic" title={sourceHint}>
                            from{" "}
                            {itemType === "post" &&
                            row.source === "primary_text"
                              ? "caption"
                              : SUGGESTED_TAG_SOURCE_LABELS[row.source]}
                            {row.reason ? " · " : ""}
                          </span>
                        )}
                        {row.reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </>
  )
}
